/**
 * Notifications API response parsers
 * Transform API responses to app-friendly format
 */

/**
 * Parse notifications error response
 * @param {Object} error - Raw error response
 * @returns {Object} Parsed error data
 */
export const parseNotificationsErrorResponse = (error) => {
  const message =
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message ||
    "Something went wrong while fetching notifications.";

  return {
    success: false,
    message,
    status: error?.response?.status || error?.status || 500,
  };
};

/**
 * Parse notifications list response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed notifications list with pagination
 */
export const parseNotificationsListResponse = (response) => {
  try {
    if (!response) {
      if (import.meta.env.DEV) {
        console.warn('[Notifications Parser] Response is null or undefined');
      }
      return {
        success: false,
        message: 'Invalid response format - no data received',
        notifications: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        }
      };
    }

    // Validate response structure
    if (typeof response !== 'object' && !Array.isArray(response)) {
      if (import.meta.env.DEV) {
        console.warn('[Notifications Parser] Invalid response type:', typeof response, response);
      }
      return {
        success: false,
        message: 'Invalid response format - expected object or array',
        notifications: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        }
      };
    }

    // Handle array response directly (if response is an array)
    if (Array.isArray(response)) {
      if (import.meta.env.DEV) {
      }

      const notifications = response
        .filter(notification => notification && (notification.id !== null && notification.id !== undefined))
        .map(notification => parseNotificationItem(notification))
        .filter(item => item !== null);

      return {
        success: true,
        code: 200,
        message: 'Notifications retrieved successfully',
        notifications,
        pagination: {
          page: 1,
          limit: response.length,
          total: response.length,
          totalPages: 1
        }
      };
    }

    // API returns: { code: "NTFS0001", message: "...", result: { page, limit, total, notifications: [...] } }
    const { result, data, code, message } = response;

    // Use result if available (actual API format), fallback to data for backward compatibility
    const responseData = result || data;

    // Handle case where response has neither result nor data property
    if (!responseData) {
      if (import.meta.env.DEV) {
        console.warn('[Notifications Parser] Response object has no result or data property:', response);
      }
      return {
        success: false,
        message: 'Invalid response format - missing result or data property',
        notifications: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        }
      };
    }

    // Handle array response directly (nested in result/data)
    if (Array.isArray(responseData)) {
      if (import.meta.env.DEV) {
        console.log('[Notifications Parser] Processing data array with', responseData.length, 'items');
      }

      const notifications = responseData
        .filter(notification => notification && (notification.id !== null && notification.id !== undefined))
        .map(notification => parseNotificationItem(notification))
        .filter(item => item !== null);

      // Check if code indicates success (string codes like "NTFS0001" or numeric 200)
      const isSuccess = isSuccessCode(code);

      return {
        success: isSuccess,
        code: code || 200,
        message: message || 'Notifications retrieved successfully',
        notifications,
        pagination: {
          page: 1,
          limit: responseData.length,
          total: responseData.length,
          totalPages: 1
        }
      };
    }

    // Handle paginated response: result.notifications or data.notifications
    const notificationsArray = responseData.notifications || responseData.data || responseData.items || [];

    if (import.meta.env.DEV) {
      console.log('[Notifications Parser] Processing paginated response with', notificationsArray.length, 'items');
    }

    const notifications = notificationsArray
      .filter(notification => notification && (notification.id !== null && notification.id !== undefined))
      .map(notification => parseNotificationItem(notification))
      .filter(item => item !== null);

    const pagination = {
      page: responseData.page || responseData.current_page || 1,
      limit: responseData.limit || responseData.per_page || responseData.page_size || 10,
      total: responseData.total || responseData.total_count || 0,
      totalPages: responseData.total_pages || responseData.totalPages || Math.ceil((responseData.total || responseData.total_count || 0) / (responseData.limit || responseData.per_page || responseData.page_size || 10))
    };

    // Check if code indicates success
    const isSuccess = isSuccessCode(code) || responseData.success !== false;

    return {
      success: isSuccess,
      code: code || 200,
      message: message || responseData.message || 'Notifications retrieved successfully',
      notifications,
      pagination
    };
  } catch (error) {
    console.error('[Notifications Parser] Fatal error parsing notifications list:', error);
    console.error('[Notifications Parser] Response that caused error:', response);
    return {
      success: false,
      message: `Error parsing response: ${error?.message || 'Unknown error'}`,
      notifications: [],
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      },
      error: error?.message || 'Unknown parsing error'
    };
  }
};

/**
 * Check if code indicates success
 * Handles both string codes (e.g., "NTFS0001") and numeric codes (e.g., 200)
 * @param {string|number} code - Response code
 * @returns {boolean} True if code indicates success
 */
const isSuccessCode = (code) => {
  if (!code) return false;
  if (typeof code === 'number') {
    return code === 200 || code >= 200 && code < 300;
  }
  if (typeof code === 'string') {
    // String codes like "NTFS0001" - assume success if starts with expected prefix
    return code.startsWith('NTFS') || code === '200' || code === 'SUCCESS';
  }
  return false;
};

/**
 * Parse a single notification item
 * Handles API field names: category (IMPORTANT/GENERAL), notification_type, is_read, created_at
 * @param {Object} notification - Raw notification object from API
 * @returns {Object|null} Parsed notification or null if invalid
 */
const parseNotificationItem = (notification) => {
  try {
    // API fields: category (IMPORTANT/GENERAL uppercase), notification_type, is_read, created_at
    const category = notification.category ? notification.category.toLowerCase() : 'general';
    const notificationType = notification.notification_type || notification.type || category;

    // Map IMPORTANT category to 'important' type
    const type = category === 'important' ? 'important' : 'general';

    return {
      id: notification.id,
      title: notification.title || notification.subject || 'No title',
      description: notification.description || notification.body || notification.message || null,
      details: notification.details || notification.metadata || null,
      timestamp: formatRelativeTime(notification.created_at || notification.timestamp || notification.createdAt || ''),
      iconType: mapNotificationTypeToIconType(notificationType || category),
      notificationLogo: notification.notification_logo || null,
      isRead: notification.is_read !== undefined ? notification.is_read : notification.isRead || false,
      type: type,
      priority: notification.priority || notification.notification_type || 'normal'
    };
  } catch (itemError) {
    if (import.meta.env.DEV) {
      console.warn('[Notifications Parser] Error processing notification item:', itemError, notification);
    }
    return null;
  }
};

/**
 * Format timestamp to relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} timestamp - Timestamp from API
 * @returns {string} Formatted relative time string
 */
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';

  // If already formatted as "X hours ago", return as is
  if (typeof timestamp === 'string' && timestamp.includes('ago')) {
    return timestamp;
  }

  try {
    const dateObj = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diffMs = now - dateObj;

    if (isNaN(dateObj.getTime())) {
      return timestamp; // Return original if invalid date
    }

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return 'just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 30) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} year${years > 1 ? 's' : ''} ago`;
    }
  } catch (error) {
    return timestamp; // Return original on error
  }
};

/**
 * Map notification type/category to iconType for UI
 * @param {string} type - Notification type from API
 * @returns {string} Icon type for UI component
 */
const mapNotificationTypeToIconType = (type) => {
  if (!type) return 'chart';

  const lowerType = type.toLowerCase();

  if (lowerType.includes('security') || lowerType.includes('auth') || lowerType.includes('login')) {
    return 'security';
  }
  if (lowerType.includes('payment') || lowerType.includes('transaction') || lowerType.includes('sip') || lowerType.includes('redeem')) {
    return 'payment';
  }
  if (lowerType.includes('compliance') || lowerType.includes('kyc') || lowerType.includes('alert') || lowerType.includes('warning')) {
    return 'compliance';
  }
  if (lowerType.includes('important') || lowerType.includes('urgent') || lowerType.includes('critical')) {
    return 'compliance';
  }

  return 'chart'; // default
};

/**
 * Parse notification counts response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed notification counts
 */
export const parseNotificationCountsResponse = (response) => {
  try {
    if (!response) {
      if (import.meta.env.DEV) {
        console.warn('[Notifications Parser] Counts response is null or undefined');
      }
      return {
        success: false,
        message: 'Invalid response format - no data received',
        counts: {
          total: 0,
          unread: 0,
          general: 0,
          important: 0
        }
      };
    }

    // Validate response structure
    if (typeof response !== 'object' && !Array.isArray(response)) {
      if (import.meta.env.DEV) {
        console.warn('[Notifications Parser] Invalid counts response type:', typeof response, response);
      }
      return {
        success: false,
        message: 'Invalid response format - expected object',
        counts: {
          total: 0,
          unread: 0,
          general: 0,
          important: 0
        }
      };
    }

    // API returns: { code: "NTFS0003", message: "...", result: { important_unread_count, general_unread_count, total_unread_count } }
    const { result, data: responseData } = response;

    // Use result if available (actual API format), fallback to data for backward compatibility
    const data = result || responseData || response;
    const code = response.code || data?.code;
    const message = response.message || data?.message;

    // API field names: important_unread_count, general_unread_count, total_unread_count
    // No total count in API, only unread counts
    const importantUnread = typeof data?.important_unread_count === 'number'
      ? data.important_unread_count
      : (typeof data?.important === 'number' ? data.important : (typeof data?.important_count === 'number' ? data.important_count : 0));

    const generalUnread = typeof data?.general_unread_count === 'number'
      ? data.general_unread_count
      : (typeof data?.general === 'number' ? data.general : (typeof data?.general_count === 'number' ? data.general_count : 0));

    const totalUnread = typeof data?.total_unread_count === 'number'
      ? data.total_unread_count
      : (importantUnread + generalUnread);

    // Total is sum of unread counts (API doesn't provide total)
    const total = typeof data?.total === 'number'
      ? data.total
      : (typeof data?.total_count === 'number' ? data.total_count : totalUnread);

    if (import.meta.env.DEV) {
      console.log('[Notifications Parser] Parsed counts:', { total, unread: totalUnread, general: generalUnread, important: importantUnread });
    }

    // Check if code indicates success
    const isSuccess = isSuccessCode(code) || data?.success !== false;

    return {
      success: isSuccess,
      code: code || 200,
      message: message || 'Counts retrieved successfully',
      counts: {
        total,
        unread: totalUnread,
        general: generalUnread,
        important: importantUnread
      },
      data: data || null
    };
  } catch (error) {
    console.error('[Notifications Parser] Fatal error parsing counts:', error);
    console.error('[Notifications Parser] Response that caused error:', response);
    return {
      success: false,
      message: `Error parsing response: ${error?.message || 'Unknown error'}`,
      counts: {
        total: 0,
        unread: 0,
        general: 0,
        important: 0
      },
      error: error?.message || 'Unknown parsing error'
    };
  }
};

/**
 * Parse mark notifications read response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed mark read response
 */
export const parseMarkReadResponse = (response) => {
  try {
    if (!response) {
      if (import.meta.env.DEV) {
        console.warn('[Notifications Parser] Mark-read response is null or undefined');
      }
      return {
        success: false,
        message: 'Invalid response format - no data received',
        data: null
      };
    }

    // Validate response structure
    if (typeof response !== 'object') {
      if (import.meta.env.DEV) {
        console.warn('[Notifications Parser] Invalid mark-read response type:', typeof response, response);
      }
      return {
        success: false,
        message: 'Invalid response format - expected object',
        data: null
      };
    }

    // API returns: { code: "NTFS0002", message: "...", result: { updatedCount } }
    const { result, data: responseData, code, message } = response;

    // Use result if available (actual API format), fallback to data for backward compatibility
    const data = result || responseData;

    // Check if code indicates success
    const isSuccess = isSuccessCode(code) || data?.success !== false;

    return {
      success: isSuccess,
      code: code || 200,
      message: message || data?.message || 'Notifications marked as read successfully',
      data: data || null
    };
  } catch (error) {
    console.error('[Notifications Parser] Error parsing mark-read response:', error, response);
    return {
      success: false,
      message: `Error parsing response: ${error?.message || 'Unknown error'}`,
      data: null,
      error: error?.message || 'Unknown parsing error'
    };
  }
};
